(function (window, document) {
  'use strict';

  if (window.LFImageSequence) return;

  var MAX_DPR = 1;
  var RENDER_MAX_W = 1920;
  var RENDER_MAX_H = 1080;
  var MAX_DECODE_WIDTH = 960;
  var MAX_CACHED_FRAMES = 28;
  var MAX_NEAREST = 16;
  var MAX_CONCURRENT = 2;
  var MAX_CONCURRENT_ACTIVE = 3;
  var MAX_CONCURRENT_NEXT = 2;
  var FIRST_BURST = 8;
  var NEIGHBOR_RADIUS = 3;
  var AHEAD_COUNT = 6;
  var FRAME_STRIDE = 1;
  var NEXT_SCENE_WARM_INIT = 8;
  var SCENE3_WARM_INIT = 4;
  var SCENE4_WARM_INIT = 4;
  var CROSS_SCENE_TAIL_START = 0.82;
  var CROSS_SCENE_TAIL_EARLY = 0.62;
  var CROSS_SCENE_WARM_MAX = 8;
  var CROSS_SCENE3_WARM_MAX = 10;
  var CROSS_SCENE4_WARM_MAX = 10;
  var SCENE4_FIRST_FRAMES = 8;
  var SCENE4_KEYFRAME_RATIO = 0.08;
  var SCENE5_WARM_INIT = 4;
  var SCENE5_FIRST_FRAMES = 8;
  var SCENE5_FLIP_RATIO = 0.45;
  var CROSS_SCENE5_WARM_MAX = 10;
  var SCENE6_WARM_INIT = 4;
  var SCENE6_FIRST_FRAMES = 12;
  var SCENE6_PRELOAD_START = 0.80;
  var CROSS_SCENE6_WARM_MAX = 12;
  var FRAME_SNAP_GAP = 0.12;
  var SETTLE_EPS = 0.00008;
  var MOBILE_MAX = 1024;
  var HEIGHT_RE = /^[0-9]+(?:\.[0-9]+)?(?:vh|svh|dvh|px|%)$/;
  var PRELOAD_AHEAD_GLOBAL = 0.18;
  var PRELOAD_LOCAL_THRESHOLD = 0.45;
  var POS = {
    center: [0.5, 0.5],
    top: [0.5, 0],
    bottom: [0.5, 1],
    left: [0, 0.5],
    right: [1, 0.5]
  };

  var engines = [];
  var scrollBound = false;
  var previousScrollBehavior = '';
  var previousScrollPriority = '';
  var rafId = 0;
  var reducedMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function alignFrame(index, count) {
    var max = Math.max(0, count - 1);
    if (index <= 0) return 0;
    if (index >= max) return max;
    if (FRAME_STRIDE <= 1) return index;
    return Math.round(index / FRAME_STRIDE) * FRAME_STRIDE;
  }

  function clamp(value, min, max) {
    if (value < min) return min;
    if (value > max) return max;
    return value;
  }

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isFinite(n) ? n : fallback;
  }

  function toFloat(value, fallback) {
    var n = parseFloat(value);
    return isFinite(n) ? n : fallback;
  }

  function padIndex(value, width) {
    var n = String(Math.abs(toInt(value, 0)));
    var w = width | 0;
    if (w < 1) return n;
    while (n.length < w) n = '0' + n;
    return n;
  }

  function sanitizeHeight(value) {
    var raw = String(value || '').replace(/\s/g, '');
    return HEIGHT_RE.test(raw) ? raw : '800vh';
  }

  function posPair(name) {
    return POS[name] || POS.center;
  }

  function prefersReduced() {
    return !!(reducedMq && reducedMq.matches);
  }

  function bindMedia(mq, fn) {
    if (!mq) return;
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else if (mq.addListener) mq.addListener(fn);
  }

  function unbindMedia(mq, fn) {
    if (!mq) return;
    if (mq.removeEventListener) mq.removeEventListener('change', fn);
    else if (mq.removeListener) mq.removeListener(fn);
  }

  function parseScenes(root) {
    var node = root.querySelector('[data-lf-sequence-scenes]');
    if (!node) return [];
    try {
      var data = JSON.parse(node.textContent || '[]');
      if (!Array.isArray(data)) return [];
      return data.filter(function (scene) {
        return scene && (scene.video || scene.template) && toInt(scene.frameCount, 0) > 1;
      }).map(function (scene) {
        return {
          slug: scene.slug || '',
          label: scene.label || '',
          template: String(scene.template || ''),
          video: scene.video ? String(scene.video) : '',
          mobileTemplate: String(scene.mobileTemplate || ''),
          mobileFrameCount: toInt(scene.mobileFrameCount, 0),
          mobileEndingStartFrame: toInt(scene.mobileEndingStartFrame, -1),
          mobileEndingImage: String(scene.mobileEndingImage || ''),
          frameCount: Math.max(2, toInt(scene.frameCount, 60)),
          startTime: Math.max(0, toFloat(scene.startTime, 0)),
          endTime: scene.endTime == null ? null : toFloat(scene.endTime, null)
        };
      });
    } catch (err) {
      return [];
    }
  }

  function resolveGlobal(globalProgress, sceneCount) {
    if (sceneCount < 1) return { sceneIndex: 0, localProgress: 0 };
    if (sceneCount === 1) {
      return { sceneIndex: 0, localProgress: clamp(globalProgress, 0, 1) };
    }

    var weight = 1 / sceneCount;
    var sceneIndex = Math.floor(globalProgress / weight);
    if (sceneIndex >= sceneCount) sceneIndex = sceneCount - 1;
    if (globalProgress >= 1) sceneIndex = sceneCount - 1;

    var localStart = sceneIndex * weight;
    var localProgress = (globalProgress - localStart) / weight;
    return { sceneIndex: sceneIndex, localProgress: clamp(localProgress, 0, 1) };
  }

  function FrameLoader(opts) {
    this.frameCount = opts.frameCount;
    this.buildUrl = opts.buildUrl;
    this.maxConcurrent = opts.maxConcurrent || MAX_CONCURRENT;
    this.onFrameLoaded = opts.onFrameLoaded || null;
    this.frames = new Array(this.frameCount);
    this.inFlight = 0;
    this.currentIndex = 0;
    this.direction = 1;
    this.generation = 1;
    this.destroyed = false;
    this.idleTimer = 0;
    this.idleHandle = 0;
    this.warmThrough = -1;
    this.boosted = false;
    this.idleLoad = false;
    this.priority = 0;
    this.decodeWidth = MAX_DECODE_WIDTH;

    var i;
    for (i = 0; i < this.frameCount; i++) {
      this.frames[i] = { status: 'empty', img: null };
    }
  }

  FrameLoader.prototype.get = function (index) {
    var slot = this.frames[index];
    return slot && slot.status === 'loaded' ? slot.img : null;
  };

  FrameLoader.prototype.getNearest = function (index) {
    var count = this.frameCount;
    var clamped = clamp(toInt(index, 0), 0, count - 1);
    var direct = this.get(clamped);
    if (direct) return { img: direct, index: clamped };

    var d;
    var limit = Math.min(MAX_NEAREST, count);
    for (d = 1; d < limit; d++) {
      if (clamped + d < count) {
        direct = this.get(clamped + d);
        if (direct) return { img: direct, index: clamped + d };
      }
      if (clamped - d >= 0) {
        direct = this.get(clamped - d);
        if (direct) return { img: direct, index: clamped - d };
      }
    }
    return null;
  };

  FrameLoader.prototype.releaseSlot = function (slot) {
    if (!slot) return;
    if (slot.img && typeof slot.img.close === 'function') {
      try { slot.img.close(); } catch (err) {}
    }
    slot.img = null;
  };

  FrameLoader.prototype.evict = function () {
    var current = this.currentIndex;
    var keep = {};
    var i;
    var loaded = 0;
    var radius = NEIGHBOR_RADIUS + AHEAD_COUNT;

    keep[0] = 1;
    keep[this.frameCount - 1] = 1;
    for (i = Math.max(0, current - NEIGHBOR_RADIUS); i <= Math.min(this.frameCount - 1, current + radius); i++) {
      keep[i] = 1;
    }
    if (this.warmThrough >= 0) {
      for (i = 0; i <= this.warmThrough && i < this.frameCount; i++) keep[i] = 1;
    }

    for (i = 0; i < this.frameCount; i++) {
      if (this.frames[i].status === 'loaded') loaded += 1;
    }

    for (i = 0; i < this.frameCount && loaded > MAX_CACHED_FRAMES; i++) {
      if (this.frames[i].status !== 'loaded' || keep[i]) continue;
      this.releaseSlot(this.frames[i]);
      this.frames[i].status = 'empty';
      loaded -= 1;
    }

    if (loaded <= MAX_CACHED_FRAMES) return;

    var extra = [];
    for (i = 0; i < this.frameCount; i++) {
      if (this.frames[i].status === 'loaded' && i !== current && i !== 0) extra.push(i);
    }
    extra.sort(function (a, b) {
      return Math.abs(b - current) - Math.abs(a - current);
    });
    for (i = 0; i < extra.length && loaded > MAX_CACHED_FRAMES; i++) {
      this.releaseSlot(this.frames[extra[i]]);
      this.frames[extra[i]].status = 'empty';
      loaded -= 1;
    }
  };

  FrameLoader.prototype.setBoosted = function (boosted) {
    this.boosted = !!boosted;
    this.maxConcurrent = boosted ? MAX_CONCURRENT_ACTIVE : MAX_CONCURRENT;
    this.pump(false);
  };

  FrameLoader.prototype.setPriority = function (priority) {
    var p = priority | 0;
    if (p === this.priority) return;
    this.priority = p;
    if (p >= 2) this.maxConcurrent = MAX_CONCURRENT_ACTIVE;
    else if (p === 1) this.maxConcurrent = MAX_CONCURRENT_NEXT;
    else this.maxConcurrent = this.boosted ? MAX_CONCURRENT_ACTIVE : MAX_CONCURRENT;
    this.pump(false);
  };

  FrameLoader.prototype.setIdleLoad = function (enabled) {
    this.idleLoad = !!enabled;
    if (enabled) return;
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = 0;
    }
    if (this.idleHandle && window.cancelIdleCallback) {
      window.cancelIdleCallback(this.idleHandle);
      this.idleHandle = 0;
    }
  };

  FrameLoader.prototype.warm = function (throughIndex) {
    this.warmThrough = clamp(toInt(throughIndex, 0), 0, this.frameCount - 1);
    this.pump(false);
  };

  FrameLoader.prototype.setCurrent = function (index, direction) {
    var next = clamp(toInt(index, 0), 0, this.frameCount - 1);
    var jumped = Math.abs(next - this.currentIndex) > 8;
    this.currentIndex = next;
    if (direction) this.direction = direction;
    if (jumped) this.cancelFar();
    this.pump(false);
  };

  FrameLoader.prototype.cancelFar = function () {
    var maxDist = NEIGHBOR_RADIUS + AHEAD_COUNT + 6;
    var i;
    for (i = 0; i < this.frameCount; i++) {
      var slot = this.frames[i];
      if (!slot || slot.status !== 'loading' || !slot.req) continue;
      if (Math.abs(i - this.currentIndex) <= maxDist) continue;
      if (this.warmThrough >= 0 && i <= this.warmThrough) continue;
      slot.req.onload = null;
      slot.req.onerror = null;
      try { slot.req.src = ''; } catch (err) {}
      slot.req = null;
      slot.status = 'empty';
      this.inFlight = Math.max(0, this.inFlight - 1);
    }
  };

  FrameLoader.prototype.start = function () {
    this.pump(false);
  };

  FrameLoader.prototype.destroy = function () {
    this.destroyed = true;
    this.generation += 1;
    this.onFrameLoaded = null;
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = 0;
    }
    if (this.idleHandle && window.cancelIdleCallback) {
      window.cancelIdleCallback(this.idleHandle);
      this.idleHandle = 0;
    }
    var i;
    for (i = 0; i < this.frames.length; i++) {
      var request = this.frames[i].req;
      if (request) {
        request.onload = null;
        request.onerror = null;
        try { request.src = ''; } catch (err) {}
        this.frames[i].req = null;
      }
      this.releaseSlot(this.frames[i]);
      this.frames[i].status = 'empty';
    }
  };

  FrameLoader.prototype.nextIndex = function (allowIdle) {
    var count = this.frameCount;
    var current = this.currentIndex;
    var dir = this.direction >= 0 ? 1 : -1;
    var seen = new Array(count);
    var list = [];
    var i;
    var d;

    function add(index) {
      index = alignFrame(index, count);
      if (index < 0 || index >= count || seen[index]) return;
      seen[index] = 1;
      list.push(index);
    }

    add(0);
    if (this.priority >= 1) {
      var burst = this.priority >= 2 ? FIRST_BURST : Math.min(FIRST_BURST, SCENE4_FIRST_FRAMES);
      for (i = 1; i < burst && i < count; i++) add(i);
    }

    if (this.warmThrough >= 0) {
      for (i = 0; i <= this.warmThrough && i < count; i++) add(i);
    }

    if (this.priority >= 1) {
      add(current);
      for (d = 1; d <= NEIGHBOR_RADIUS; d++) {
        add(current + d);
        add(current - d);
      }

      var ahead = this.priority >= 2 ? AHEAD_COUNT : 12;
      for (i = 1; i <= ahead; i++) add(current + dir * i);
    }

    if (allowIdle && this.priority >= 2) {
      for (i = 1; i <= AHEAD_COUNT * 2; i++) add(current + dir * i);
    }

    for (i = 0; i < list.length; i++) {
      if (this.frames[list[i]].status === 'empty') return list[i];
    }
    return -1;
  };

  FrameLoader.prototype.scheduleIdle = function () {
    if (!this.idleLoad) return;
    var self = this;
    if (this.destroyed || this.idleTimer || this.idleHandle) return;

    function run() {
      self.idleTimer = 0;
      self.idleHandle = 0;
      if (self.destroyed) return;
      self.pump(true);
    }

    if (window.requestIdleCallback) {
      this.idleHandle = window.requestIdleCallback(run, { timeout: 1600 });
    } else {
      this.idleTimer = setTimeout(run, 120);
    }
  };

  FrameLoader.prototype.pump = function (allowIdle) {
    if (this.destroyed) return;
    if (allowIdle && !this.idleLoad) return;

    while (this.inFlight < this.maxConcurrent) {
      var index = this.nextIndex(allowIdle);
      if (index < 0) break;
      this.load(index);
    }

    if (!allowIdle && this.idleLoad) this.scheduleIdle();
  };

  FrameLoader.prototype.load = function (index) {
    var slot = this.frames[index];
    if (!slot || slot.status !== 'empty') return;

    var self = this;
    var gen = this.generation;
    var img = new Image();
    var url = this.buildUrl(index);

    slot.status = 'loading';
    slot.req = img;
    this.inFlight += 1;
    img.decoding = 'async';

    img.onload = function () {
      if (self.destroyed || gen !== self.generation || slot.status !== 'loading' || slot.req !== img) return;
      self.inFlight = Math.max(0, self.inFlight - 1);
      slot.status = 'loaded';
      slot.img = img;
      slot.req = null;
      if (self.onFrameLoaded) self.onFrameLoaded(index, img);
      self.evict();
      self.pump(false);
    };

    img.onerror = function () {
      if (self.destroyed || gen !== self.generation || slot.status !== 'loading' || slot.req !== img) return;
      self.inFlight = Math.max(0, self.inFlight - 1);
      slot.status = 'error';
      slot.img = null;
      slot.req = null;
      self.pump(false);
    };

    try {
      img.src = url;
    } catch (err) {
      this.inFlight = Math.max(0, this.inFlight - 1);
      slot.status = 'error';
      slot.req = null;
      this.pump(false);
    }
  };

  function makeSeqVideo() {
    var v = document.createElement('video');
    v.muted = true;
    v.defaultMuted = true;
    v.autoplay = false;
    v.playsInline = true;
    v.preload = 'auto';
    v.controls = false;
    v.tabIndex = -1;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.setAttribute('disablepictureinpicture', '');
    v.className = 'lf-sequence__video';
    v.style.opacity = '0';
    return v;
  }

  function DualVideoPlayer(sticky, urls, frameCounts, onReady, onError, onPresented, ranges) {
    this.sticky = sticky;
    this.urls = urls || [];
    this.frameCounts = frameCounts || [];
    this.ranges = ranges || [];
    this.onReady = onReady || null;
    this.onError = onError || null;
    this.onPresented = onPresented || null;
    this.videos = [];
    this.ready = [];
    this.states = [];
    this.scene = 0;
    this.targetScene = 0;
    this.targetLocal = 0;
    this.entryTime = null;
    this.entryTimes = [];
    this.pending = -1;
    this.shown = false;
    this.destroyed = false;
    this.failed = false;
    this.lastCopyKey = '';
    for (var i = 0; i < this.urls.length; i++) {
      var video = makeSeqVideo();
      video.setAttribute('data-scene', String(i));
      video.setAttribute('aria-hidden', 'true');
      video.style.zIndex = '1';
      if (i === 0) video.setAttribute('fetchpriority', 'high');
      sticky.appendChild(video);
      this.videos.push(video);
      this.ready.push(false);
      this.states.push({frameHandle: null, loadTimer: 0, confirmTimer: 0, seekTimer: 0, primeTimer: 0, priming: false, primeAttempts: 0, seeking: false, requestedTime: null, loadError: false, retryUsed: false, lastTime: null, listeners: null});
      this.bind(video, i);
    }
    this.ensureSrc(0);
    this.ensureSrc(1);
    this.watchTarget(0);
    var self = this;
    this.onGesture = function () { self.prime(self.targetScene, true); };
    sticky.addEventListener('touchend', this.onGesture, {passive: true});
    sticky.addEventListener('click', this.onGesture);
  }

  // A mobile browser may preload metadata only. Start its decoder once, then
  // pause on the first usable frame; scrolling remains the playback clock.
  DualVideoPlayer.prototype.prime = function (index, userGesture) {
    var video = this.videos[index];
    var state = this.states[index];
    if (this.destroyed || index !== this.targetScene || !video || !video.getAttribute('src') || video.readyState >= 2 || state.priming) return;
    if (state.lastTime !== null) return; // An already decoded scene only needs seeking.
    if (state.primeAttempts >= (userGesture ? 3 : 1)) return;
    state.primeAttempts += 1;
    state.priming = true;
    var self = this;
    var attempt = state.primeAttempts;
    function settled() {
      if (self.destroyed || attempt !== state.primeAttempts || !state.priming) return;
      self.finishPrime(index);
    }
    state.primeTimer = setTimeout(settled, 2500);
    try {
      var playing = video.play();
      if (playing && typeof playing.then === 'function') playing.then(settled, settled);
    } catch (err) { settled(); }
  };

  DualVideoPlayer.prototype.finishPrime = function (index) {
    var video = this.videos[index];
    var state = this.states[index];
    if (this.destroyed || !video || !state.priming) return;
    clearTimeout(state.primeTimer);
    state.primeTimer = 0;
    state.priming = false;
    video.pause();
    if (video.readyState >= 1 && video.duration) this.ready[index] = true;
    // A rejected play is recoverable: metadata can still permit seeking, and
    // the next real touch/click can retry activation without cancelling scroll.
    this.drive(index);
  };

  DualVideoPlayer.prototype.frameDt = function (video) {
    var scene = parseInt(video.getAttribute('data-scene'), 10);
    var count = this.frameCounts[scene] || 0;
    return count >= 2 && video.duration ? video.duration / count : 0.0417;
  };

  DualVideoPlayer.prototype.frameEps = function (video) {
    return this.frameDt(video) * 0.32;
  };

  // Clip endpoints use the original media frame grid; frameCounts always
  // describe the complete source, not the number of frames in the selected clip.
  DualVideoPlayer.prototype.frameRange = function (video) {
    var duration = video.duration || 0;
    var index = parseInt(video.getAttribute('data-scene'), 10);
    var count = this.frameCounts[index] || 0;
    var range = this.ranges[index] || {};
    var limit = Math.max(0, duration - 0.001);
    var start = clamp(toFloat(range.startTime, 0), 0, limit);
    var end = range.endTime == null ? limit : clamp(toFloat(range.endTime, limit), start, limit);
    var dt = count >= 2 && duration ? duration / count : 0;
    var first = dt ? Math.max(0, Math.ceil(start / dt - 0.000001)) : 0;
    var last = dt ? Math.min(count - 1, Math.floor(end / dt - 0.2 + 0.000001)) : -1;
    return {start: start, end: end, dt: dt, first: first, last: last};
  };

  DualVideoPlayer.prototype.timeFor = function (video, local) {
    var range = this.frameRange(video);
    local = clamp(local, 0, 1);
    if (range.dt && range.last >= range.first) {
      var frame = Math.round(range.first + local * (range.last - range.first));
      return clamp((frame + 0.2) * range.dt, range.start, range.end);
    }
    return range.start + local * (range.end - range.start);
  };

  DualVideoPlayer.prototype.localFor = function (index, time) {
    var range = this.frameRange(this.videos[index]);
    if (range.dt && range.last > range.first) {
      var frame = Math.round(time / range.dt - 0.2);
      return clamp((frame - range.first) / (range.last - range.first), 0, 1);
    }
    return range.end > range.start ? clamp((time - range.start) / (range.end - range.start), 0, 1) : 0;
  };

  DualVideoPlayer.prototype.bind = function (video, index) {
    var self = this;
    function metadata() {
      if (self.destroyed || video.readyState < 1 || !video.duration) return;
      self.ready[index] = true;
      self.prime(index, false);
      self.drive(index);
    }
    function loaded() {
      if (self.destroyed || video.readyState < 2) return;
      var state = self.states[index];
      clearTimeout(state.loadTimer);
      state.loadTimer = 0;
      state.loadError = false;
      self.ready[index] = true;
      self.finishPrime(index);
      self.confirmDecoded(index, video.currentTime);
      self.drive(index);
    }
    function seeked() {
      if (self.destroyed || video.seeking) return;
      self.states[index].seeking = false;
      self.confirmDecoded(index, video.currentTime);
    }
    function failed() {
      if (self.destroyed) return;
      self.states[index].loadError = true;
      self.ready[index] = false;
      if (index === self.targetScene) self.fail(index, 'media-error');
    }
    this.states[index].listeners = {metadata: metadata, loaded: loaded, seeked: seeked, failed: failed};
    video.addEventListener('loadedmetadata', metadata);
    video.addEventListener('loadeddata', loaded);
    video.addEventListener('canplay', loaded);
    video.addEventListener('seeked', seeked);
    video.addEventListener('error', failed);
  };

  // A single callback is armed for a load/seek; paused videos do not create a polling loop.
  DualVideoPlayer.prototype.armFrame = function (index) {
    var video = this.videos[index];
    var state = this.states[index];
    if (this.destroyed || !video || !video.requestVideoFrameCallback || state.frameHandle !== null) return;
    var self = this;
    state.frameHandle = video.requestVideoFrameCallback(function (_, metadata) {
      state.frameHandle = null;
      if (self.destroyed) return;
      state.lastTime = metadata.mediaTime;
      self.present(index, metadata.mediaTime);
      if (!video.seeking) {
        state.seeking = false;
        if (Math.abs(metadata.mediaTime - video.currentTime) < self.frameDt(video) * 0.8) {
          clearTimeout(state.confirmTimer);
          state.confirmTimer = 0;
          clearTimeout(state.seekTimer);
          state.seekTimer = 0;
        }
        self.drive(index);
      }
    });
  };

  // seeked is the fallback for browsers without rVFC, and for a paused/hidden
  // video whose compositor callback is suppressed. Never sample a seek target.
  DualVideoPlayer.prototype.confirmDecoded = function (index, time) {
    if (this.destroyed) return;
    var video = this.videos[index];
    var state = this.states[index];
    if (!video || video.seeking) return;
    var self = this;
    function commit() {
      state.confirmTimer = 0;
      if (self.destroyed || video.seeking || video.readyState < 2 || Math.abs(video.currentTime - time) > self.frameEps(video)) return;
      state.lastTime = time;
      state.seeking = false;
      clearTimeout(state.seekTimer);
      state.seekTimer = 0;
      self.present(index, time);
      self.drive(index);
    }
    clearTimeout(state.confirmTimer);
    state.confirmTimer = 0;
    if (!video.requestVideoFrameCallback) {
      commit();
      return;
    }
    if (state.lastTime !== null && Math.abs(state.lastTime - time) < this.frameDt(video) * 0.75) {
      this.present(index, state.lastTime);
      this.drive(index);
      return;
    }
    this.armFrame(index);
    state.confirmTimer = setTimeout(commit, 120);
  };

  DualVideoPlayer.prototype.present = function (index, time) {
    if (this.destroyed || !this.ready[index]) return;
    var video = this.videos[index];
    if (video.readyState < 2 || this.states[index].priming) return;
    // A slow scene may only replace the visible scene if it is still requested,
    // and its entry seek has actually produced a frame.
    if (!this.shown || index !== this.scene) {
      if (index !== this.targetScene || this.entryTime === null) return;
      var entryMatches = false;
      for (var candidate = 0; candidate < this.entryTimes.length; candidate++) {
        if (Math.abs(time - this.entryTimes[candidate]) <= this.frameDt(video) * 0.8) entryMatches = true;
      }
      if (!entryMatches) return;
      for (var i = 0; i < this.videos.length; i++) {
        this.videos[i].style.opacity = i === index ? '1' : '0';
        this.videos[i].style.zIndex = i === index ? '3' : '1';
      }
      this.scene = index;
      this.pending = -1;
      if (!this.shown) {
        this.shown = true;
        if (this.onReady) this.onReady();
      }
    }
    if (index !== this.scene) return;
    var local = this.localFor(index, time);
    var key = index + ':' + local.toFixed(6);
    if (key === this.lastCopyKey) return;
    this.lastCopyKey = key;
    if (this.onPresented) this.onPresented(index, local);
  };

  // One seek can finish while newer scroll positions arrive. Only the latest
  // target is retained, and the next seek jumps directly there in either direction.
  DualVideoPlayer.prototype.drive = function (index) {
    if (this.destroyed || index !== this.targetScene) return;
    var video = this.videos[index];
    var state = this.states[index];
    if (!video || !this.ready[index] || !video.duration || state.priming) return;
    var time = this.timeFor(video, this.targetLocal);
    if (this.entryTime === null) {
      this.entryTime = time;
      this.entryTimes.push(time);
    }
    // The browser's state is authoritative. A coalesced/same-frame seek need not
    // emit seeked, so a private boolean must never lock future scroll updates.
    state.seeking = !!video.seeking;
    // Reassigning currentTime aborts an in-flight seek. Keep only the latest
    // target while it decodes, then drive it from seeked/the bounded watchdog.
    if (video.seeking) { this.watchSeek(index); return; }
    if (Math.abs(video.currentTime - time) <= this.frameEps(video)) {
      if (video.seeking || video.readyState < 2) {
        this.watchSeek(index);
      } else if (state.lastTime !== null && Math.abs(state.lastTime - video.currentTime) < this.frameDt(video) * 0.8) {
        this.present(index, state.lastTime);
      } else if (!state.confirmTimer) {
        this.confirmDecoded(index, video.currentTime);
      }
      return;
    }
    clearTimeout(state.confirmTimer);
    state.confirmTimer = 0;
    state.seeking = true;
    state.requestedTime = time;
    if (!this.shown || index !== this.scene) {
      this.entryTimes.push(time);
      if (this.entryTimes.length > 12) this.entryTimes.shift();
    }
    this.armFrame(index);
    this.watchSeek(index);
    try { video.currentTime = time; } catch (err) {
      state.seeking = false;
      this.fail(index, 'media-error');
    }
  };

  DualVideoPlayer.prototype.setProgress = function (sceneIndex, local) {
    if (this.destroyed || !this.videos.length) return;
    sceneIndex = clamp(sceneIndex, 0, this.videos.length - 1);
    if (sceneIndex !== this.targetScene) {
      var previous = this.states[this.targetScene];
      clearTimeout(previous.loadTimer);previous.loadTimer = 0;
      clearTimeout(previous.seekTimer);previous.seekTimer = 0;
      clearTimeout(previous.primeTimer);previous.primeTimer = 0;
      if (previous.priming) this.videos[this.targetScene].pause();
      previous.priming = false;
      this.targetScene = sceneIndex;
      this.entryTime = null;
      this.entryTimes = [];
    }
    this.targetLocal = clamp(local, 0, 1);
    this.pending = this.shown && this.scene === sceneIndex ? -1 : sceneIndex;
    var state = this.states[sceneIndex];
    if (state.loadError && !state.retryUsed) {
      state.retryUsed = true;
      state.loadError = false;
      try { this.videos[sceneIndex].load(); } catch (err) {}
    }
    this.ensureSrc(sceneIndex);
    this.ensureSrc(sceneIndex + 1);
    this.watchTarget(sceneIndex);
    this.prime(sceneIndex, false);
    this.drive(sceneIndex);
  };

  DualVideoPlayer.prototype.ensureSrc = function (index) {
    var video = this.videos[index];
    if (!video || this.destroyed || !this.urls[index] || video.getAttribute('src')) return;
    var self = this;
    this.armFrame(index);
    try { video.src = this.urls[index]; } catch (err) {
      setTimeout(function () {
        if (self.destroyed) return;
        self.states[index].loadError = true;
        if (index === self.targetScene) self.fail(index, 'media-error');
      }, 0);
    }
  };

  DualVideoPlayer.prototype.watchTarget = function (index) {
    if (this.destroyed || index !== this.targetScene) return;
    var state = this.states[index];
    if (this.videos[index].readyState >= 2 || state.loadTimer) return;
    var self = this;
    state.loadTimer = setTimeout(function () {
      state.loadTimer = 0;
      if (!self.destroyed && index === self.targetScene && self.videos[index].readyState < 2) self.fail(index, 'load-timeout');
    }, 20000);
  };

  // Recovery is event-driven plus a bounded watchdog, never an idle RAF loop.
  // It also covers readyState dropping to HAVE_METADATA during a distant seek.
  DualVideoPlayer.prototype.watchSeek = function (index) {
    if (this.destroyed || index !== this.targetScene) return;
    var state = this.states[index];
    if (state.seekTimer) return;
    var self = this;
    var attempts = 0;
    function check() {
      state.seekTimer = 0;
      if (self.destroyed || index !== self.targetScene) return;
      var video = self.videos[index];
      if (!video.seeking && video.readyState >= 2) {
        state.seeking = false;
        self.confirmDecoded(index, video.currentTime);
        self.drive(index);
        return;
      }
      attempts += 1;
      if (attempts >= 40) { self.fail(index, 'seek-timeout'); return; }
      state.seekTimer = setTimeout(check, 500);
    }
    state.seekTimer = setTimeout(check, 500);
  };

  DualVideoPlayer.prototype.fail = function (index, reason) {
    if (this.destroyed || this.failed) return;
    this.failed = true;
    var notify = this.onError;
    this.destroy();
    if (notify) notify({sceneIndex: index, reason: reason});
  };

  DualVideoPlayer.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;
    this.onReady = null;
    this.onError = null;
    this.onPresented = null;
    this.pending = -1;
    this.sticky.removeEventListener('touchend', this.onGesture);
    this.sticky.removeEventListener('click', this.onGesture);
    for (var i = 0; i < this.videos.length; i++) {
      var video = this.videos[i];
      var state = this.states[i];
      clearTimeout(state.loadTimer);
      clearTimeout(state.confirmTimer);
      clearTimeout(state.seekTimer);
      clearTimeout(state.primeTimer);
      if (state.frameHandle !== null && video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(state.frameHandle);
      video.removeEventListener('loadedmetadata', state.listeners.metadata);
      video.removeEventListener('loadeddata', state.listeners.loaded);
      video.removeEventListener('canplay', state.listeners.loaded);
      video.removeEventListener('seeked', state.listeners.seeked);
      video.removeEventListener('error', state.listeners.failed);
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
        if (video.parentNode) video.parentNode.removeChild(video);
      } catch (err) {}
    }
    this.videos = [];
    this.ready = [];
    this.states = [];
  };
  function SequenceEngine(root) {
    this.root = root;
    this.sticky = root.querySelector('.lf-sequence__sticky');
    this.canvas = root.querySelector('[data-lf-sequence-canvas]');
    this.ctx = null;

    this.scenes = parseScenes(root);
    this.startIndex = toInt(root.getAttribute('data-start-index'), 1);
    this.padding = Math.max(1, toInt(root.getAttribute('data-padding'), 4));
    this.easing = clamp(toFloat(root.getAttribute('data-easing'), 0.12), 0.04, 0.4);
    this.posDesktop = posPair(root.getAttribute('data-pos-desktop'));
    this.posMobile = posPair(root.getAttribute('data-pos-mobile'));

    this.targetProgress = 0;
    this.currentProgress = 0;
    this.activeSceneIndex = 0;
    this.frameIndex = 0;
    this.lastDrawnKey = '';
    this.lastDrawnIndex = -1;
    this.lastPaintedScene = -1;
    this.lastImage = null;
    this.holdFrame = null;
    this.holdCanvas = null;
    this.holdScene = -1;
    this.direction = 1;
    this.markedReady = false;
    this.reducedMotion = prefersReduced();

    this.sectionTop = 0;
    this.sectionHeight = 0;
    this.viewportHeight = 0;
    this.scrollable = 1;
    this.cssW = 0;
    this.cssH = 0;
    this.renderW = 0;
    this.renderH = 0;
    this.dpr = 1;
    this.posX = 0.5;
    this.posY = 0.5;

    this.needsMeasure = true;
    this.needsCanvasResize = true;
    this.needsRedraw = true;
    this.destroyed = false;
    this.loaders = [];
    this.clips = [];
    this.player = null;
    this.copy = null;
    this.useVideo = false;
    this.ro = null;
    this.prioritySceneIndex = -1;

    this.scrollHeight = sanitizeHeight(root.getAttribute('data-scroll-height'));
    this.scrollViewportWidth = 0;
    this.syncScrollHeight();

    this.onReduced = this.onReduced.bind(this);
    this.onObserved = this.onObserved.bind(this);

    if (this.scenes.length) {
      this.useVideo = this.scenes.every(function (scene) { return !!scene.video; });
      if (this.useVideo) {
        this.initVideos();
      } else if (this.canvas) {
        this.ctx = this.canvas.getContext('2d', { alpha: false, desynchronized: true });
        if (!this.ctx) this.ctx = this.canvas.getContext('2d', { alpha: false });
        if (this.ctx) this.initLoaders();
      }
    }

    bindMedia(reducedMq, this.onReduced);
    this.observe();
    if (window.LFSequenceCopy) this.copy = window.LFSequenceCopy.create(root);
    if (this.useVideo && this.copy) this.copy.sync(0, 0);
  }

  SequenceEngine.prototype.buildUrl = function (template, frameIndex) {
    var fileIndex = this.startIndex + frameIndex;
    var token = padIndex(fileIndex, this.padding);
    return template.split('{index}').join(token).split('{INDEX}').join(token);
  };

  SequenceEngine.prototype.initLoaders = function () {
    var self = this;
    var i;

    for (i = 0; i < this.scenes.length; i++) {
      (function (sceneIndex) {
        var scene = self.scenes[sceneIndex];
        var loader = new FrameLoader({
          frameCount: scene.frameCount,
          maxConcurrent: MAX_CONCURRENT,
          buildUrl: function (frameIndex) {
            return self.buildUrl(scene.template, frameIndex);
          },
          onFrameLoaded: function (index) {
            self.onFrameLoaded(sceneIndex, index);
          }
        });
        loader.idleLoad = false;
        loader.priority = sceneIndex === 0 ? 2 : 0;
        if (sceneIndex === 0) loader.start();
        self.loaders.push(loader);
      })(i);
    }

    if (this.loaders.length > 1) {
      this.loaders[1].warm(NEXT_SCENE_WARM_INIT);
      this.loaders[1].setCurrent(0, 1);
    }
  };

  SequenceEngine.prototype.initVideos = function () {
    var self = this;
    var urls = [];
    var counts = [];
    var ranges = [];
    var i;
    for (i = 0; i < this.scenes.length; i++) {
      urls.push(this.scenes[i].video);
      counts.push(this.scenes[i].frameCount);
      ranges.push({startTime: this.scenes[i].startTime, endTime: this.scenes[i].endTime});
    }
    this.root.classList.add('is-video');
    if (!this.sticky) return;
    var onReady = function () {
      if (self.destroyed) return;
      if (!self.markedReady) {
        // If loading was slow and the visitor already scrolled past the opening,
        // do not suddenly insert a long pinned area ahead of the content.
        var top = parseFloat(window.getComputedStyle(self.sticky).top) || 0;
        if (self.root.getBoundingClientRect().bottom <= top + 1) {
          setTimeout(function () {
            if (!self.destroyed) self.root.dispatchEvent(new CustomEvent('lf-sequence-error', {bubbles: true, detail: {reason: 'passed-loading-hero'}}));
          }, 0);
          return;
        }
        self.markedReady = true;
        self.root.classList.add('is-ready');
        self.needsMeasure = true;
      }
      startRaf();
    };
    var onError = function (detail) {
      if (self.destroyed || !self.root) return;
      var failedRoot = self.root;
      destroyRoot(failedRoot);
      failedRoot.dispatchEvent(new CustomEvent('lf-sequence-error', { bubbles: true, detail: detail }));
    };
    var onPresented = function (sceneIndex, localProgress) {
      if (self.destroyed) return;
      self.activeSceneIndex = sceneIndex;
      self.frameIndex = Math.round(localProgress * (self.scenes[sceneIndex].frameCount - 1));
      if (self.copy) self.copy.sync(sceneIndex, localProgress);
      startRaf();
    };
    // Mobile browsers can acknowledge a paused-video seek without updating its
    // visible layer. Decode ordinary image frames instead, on the same timeline.
    var mobileFrames = window.innerWidth <= MOBILE_MAX && this.scenes.every(function (scene) {
      return !!scene.mobileTemplate && scene.mobileFrameCount > 1;
    });
    if (mobileFrames) {
      if (!window.LFMobileFramePlayer) { setTimeout(function () { onError({reason: 'frame-player-unavailable'}); }, 0); return; }
      this.root.setAttribute('data-lf-playback', 'image-frames');
      this.player = new window.LFMobileFramePlayer(this.sticky, this.scenes, onReady, onError, onPresented);
    } else {
      this.root.setAttribute('data-lf-playback', 'video');
      this.player = new DualVideoPlayer(this.sticky, urls, counts, onReady, onError, onPresented, ranges);
    }
  };

  SequenceEngine.prototype.syncLoaderPriority = function (sceneIndex) {
    var i;
    var changed = sceneIndex !== this.prioritySceneIndex;
    this.prioritySceneIndex = sceneIndex;

    for (i = 0; i < this.loaders.length; i++) {
      var isActive = i === sceneIndex;
      var isNext = i === sceneIndex + 1;
      var isPrev = i === sceneIndex - 1;
      var priority = isActive ? 2 : (isNext || isPrev ? 1 : 0);

      if (changed || isActive) {
        this.loaders[i].setBoosted(isActive);
      }
      this.loaders[i].setPriority(priority);
      this.loaders[i].setIdleLoad(false);
      if (!isActive && !isNext && !isPrev) {
        this.loaders[i].warmThrough = Math.min(this.loaders[i].warmThrough, 7);
      }
    }
  };

  SequenceEngine.prototype.observe = function () {
    if (!window.ResizeObserver) return;
    this.ro = new ResizeObserver(this.onObserved);
    if (this.sticky) this.ro.observe(this.sticky);
    if (this.root) this.ro.observe(this.root);
  };

  SequenceEngine.prototype.onObserved = function () {
    this.needsMeasure = true;
    this.needsCanvasResize = true;
    startRaf();
  };

  SequenceEngine.prototype.onReduced = function () {
    this.reducedMotion = prefersReduced();
    startRaf();
  };

  SequenceEngine.prototype.onFrameLoaded = function (sceneIndex, index) {
    if (this.destroyed) return;
    var needed = sceneIndex === this.activeSceneIndex && Math.abs(index - this.frameIndex) <= 2;
    var crossing = sceneIndex !== this.lastPaintedScene && (
      (this.direction >= 0 && index === 0) ||
      (this.direction < 0 && this.loaders[sceneIndex] && index === this.loaders[sceneIndex].frameCount - 1)
    );
    if (needed || crossing || (sceneIndex === 0 && index === 0 && !this.lastImage)) {
      this.needsRedraw = true;
      startRaf();
    }
  };

  // In-app browser toolbars can resize the entire web view while scrolling.
  // Freeze the phone scroll distance until its width changes (e.g. rotation),
  // so a toolbar-height change cannot move every section below a 1200vh hero.
  SequenceEngine.prototype.syncScrollHeight = function () {
    var width = window.innerWidth || document.documentElement.clientWidth;
    if (width === this.scrollViewportWidth) return;
    this.scrollViewportWidth = width;
    var height = this.scrollHeight;
    if (width <= MOBILE_MAX && /(?:s|d)?vh$/.test(height)) {
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      height = Math.round(parseFloat(height) * viewportHeight / 100) + 'px';
    }
    this.root.style.setProperty('--lf-sequence-scroll', height);
  };

  SequenceEngine.prototype.measure = function () {
    this.syncScrollHeight();
    var rect = this.root.getBoundingClientRect();
    var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var stickyTop = this.sticky ? parseFloat(window.getComputedStyle(this.sticky).top) : 0;
    if (!isFinite(stickyTop)) stickyTop = 0;
    this.sectionTop = rect.top + scrollY - stickyTop;
    this.sectionHeight = this.root.offsetHeight;
    this.viewportHeight = (this.sticky && this.sticky.clientHeight) || window.innerHeight;
    this.scrollable = Math.max(1, this.sectionHeight - this.viewportHeight);
    this.syncScroll(scrollY);
    this.needsMeasure = false;

    this.isMobile = window.innerWidth <= MOBILE_MAX;
    this.canvasBackground = window.getComputedStyle(this.root).getPropertyValue('--lf-sequence-bg').trim() || '#eee8de';
    var pair = this.isMobile ? this.posMobile : this.posDesktop;
    this.posX = pair[0];
    this.posY = pair[1];
  };

  SequenceEngine.prototype.resizeCanvas = function () {
    if (!this.canvas || !this.ctx || !this.sticky) {
      this.needsCanvasResize = false;
      return;
    }

    // CSS gives every media path the same mobile safe area. Measure that area,
    // rather than the whole sticky stage, so the fallback cannot stretch it.
    var cssW = this.canvas.clientWidth;
    var cssH = this.canvas.clientHeight;
    if (cssW < 1 || cssH < 1) {
      this.needsCanvasResize = false;
      return;
    }

    var aspect = cssW / cssH;
    var renderW;
    var renderH;
    if (aspect >= RENDER_MAX_W / RENDER_MAX_H) {
      renderW = RENDER_MAX_W;
      renderH = Math.max(1, Math.round(RENDER_MAX_W / aspect));
    } else {
      renderH = RENDER_MAX_H;
      renderW = Math.max(1, Math.round(RENDER_MAX_H * aspect));
    }

    if (cssW === this.cssW && cssH === this.cssH && renderW === this.renderW && renderH === this.renderH) {
      this.needsCanvasResize = false;
      return;
    }

    this.cssW = cssW;
    this.cssH = cssH;
    this.dpr = 1;
    this.renderW = renderW;
    this.renderH = renderH;
    this.canvas.width = renderW;
    this.canvas.height = renderH;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.imageSmoothingEnabled = true;
    if ('imageSmoothingQuality' in this.ctx) this.ctx.imageSmoothingQuality = 'high';

    var i;
    for (i = 0; i < this.loaders.length; i++) {
      this.loaders[i].decodeWidth = RENDER_MAX_W;
    }
    this.needsCanvasResize = false;
    this.needsRedraw = true;
  };

  SequenceEngine.prototype.syncScroll = function (scrollY) {
    var progress = (scrollY - this.sectionTop) / this.scrollable;
    progress = clamp(progress, 0, 1);
    if (progress > this.targetProgress) this.direction = 1;
    else if (progress < this.targetProgress) this.direction = -1;
    this.targetProgress = progress;
  };

  SequenceEngine.prototype.updateCrossScenePreload = function (globalProgress, sceneIndex, localProgress) {
    var total = this.loaders.length;
    if (total < 2) return;

    var nextIndex = sceneIndex + 1;
    var prevIndex = sceneIndex - 1;
    var sceneWeight = 1 / total;

    if (this.direction >= 0) {
      if (sceneIndex === 0 && (globalProgress > PRELOAD_AHEAD_GLOBAL || localProgress > PRELOAD_LOCAL_THRESHOLD)) {
        var loader2 = this.loaders[1];
        var warm2 = Math.min(
          this.scenes[1].frameCount - 1,
          Math.max(NEXT_SCENE_WARM_INIT, Math.floor(localProgress * NEXT_SCENE_WARM_INIT * 1.4))
        );
        loader2.warm(warm2);
        loader2.setCurrent(Math.min(warm2, this.scenes[1].frameCount - 1), 1);
      }

      if (nextIndex === 2 && sceneIndex === 1) {
        var loader3 = this.loaders[2];
        var count3 = this.scenes[2].frameCount;
        var warm3 = SCENE3_WARM_INIT;

        if (localProgress > 0.15 || globalProgress > 0.42) {
          warm3 = Math.max(warm3, Math.floor(localProgress * CROSS_SCENE3_WARM_MAX));
        }
        if (localProgress > CROSS_SCENE_TAIL_EARLY) {
          warm3 = Math.max(warm3, Math.floor((localProgress - CROSS_SCENE_TAIL_EARLY) / (1 - CROSS_SCENE_TAIL_EARLY) * CROSS_SCENE3_WARM_MAX));
        }
        if (localProgress >= CROSS_SCENE_TAIL_START) {
          var tail3 = (localProgress - CROSS_SCENE_TAIL_START) / (1 - CROSS_SCENE_TAIL_START);
          warm3 = Math.max(warm3, Math.ceil(tail3 * CROSS_SCENE3_WARM_MAX));
        }

        warm3 = Math.min(count3 - 1, warm3);
        loader3.warm(warm3);
        loader3.setCurrent(warm3, 1);
      } else if (nextIndex === 3 && sceneIndex === 2) {
        var loader4 = this.loaders[3];
        var count4 = this.scenes[3].frameCount;
        var first12 = Math.min(count4 - 1, SCENE4_FIRST_FRAMES - 1);
        var first20 = Math.min(count4 - 1, Math.max(first12, Math.ceil(count4 * SCENE4_KEYFRAME_RATIO) - 1));
        var warm4 = SCENE4_WARM_INIT;

        loader4.setCurrent(0, 1);

        if (localProgress > 0.15) {
          warm4 = Math.max(warm4, first12);
        }
        if (localProgress > CROSS_SCENE_TAIL_EARLY) {
          warm4 = Math.max(
            warm4,
            Math.floor((localProgress - CROSS_SCENE_TAIL_EARLY) / (1 - CROSS_SCENE_TAIL_EARLY) * first20)
          );
        }
        if (localProgress > CROSS_SCENE_TAIL_START) {
          var tail4 = (localProgress - CROSS_SCENE_TAIL_START) / (1 - CROSS_SCENE_TAIL_START);
          warm4 = Math.max(warm4, first20);
          warm4 = Math.max(warm4, Math.ceil(tail4 * CROSS_SCENE4_WARM_MAX));
        }

        warm4 = Math.min(count4 - 1, warm4);
        loader4.warm(warm4);
        loader4.setCurrent(Math.min(warm4, count4 - 1), 1);
      } else if (nextIndex === 4 && sceneIndex === 3) {
        var loader5 = this.loaders[4];
        var count5 = this.scenes[4].frameCount;
        var first12 = Math.min(count5 - 1, SCENE5_FIRST_FRAMES - 1);
        var first20 = Math.min(count5 - 1, Math.max(first12, Math.ceil(count5 * SCENE4_KEYFRAME_RATIO) - 1));
        var flipIndex = Math.min(count5 - 1, Math.floor(count5 * SCENE5_FLIP_RATIO));
        var warm5 = SCENE5_WARM_INIT;

        loader5.setCurrent(0, 1);

        if (localProgress > 0.15) {
          warm5 = Math.max(warm5, first12);
        }
        if (localProgress > CROSS_SCENE_TAIL_EARLY) {
          warm5 = Math.max(
            warm5,
            Math.floor((localProgress - CROSS_SCENE_TAIL_EARLY) / (1 - CROSS_SCENE_TAIL_EARLY) * first20)
          );
        }
        if (localProgress > CROSS_SCENE_TAIL_START) {
          var tail5 = (localProgress - CROSS_SCENE_TAIL_START) / (1 - CROSS_SCENE_TAIL_START);
          warm5 = Math.max(warm5, first20);
          warm5 = Math.max(warm5, Math.ceil(tail5 * CROSS_SCENE5_WARM_MAX));
          loader5.setCurrent(flipIndex, 1);
        }

        warm5 = Math.min(count5 - 1, warm5);
        loader5.warm(warm5);
        if (localProgress <= CROSS_SCENE_TAIL_START) {
          loader5.setCurrent(Math.min(warm5, count5 - 1), 1);
        }
      } else if (nextIndex === 5 && sceneIndex === 4) {
        var loader6 = this.loaders[5];
        var count6 = this.scenes[5].frameCount;
        var first12 = Math.min(count6 - 1, SCENE6_FIRST_FRAMES - 1);
        var first20 = Math.min(count6 - 1, Math.max(first12, Math.ceil(count6 * SCENE4_KEYFRAME_RATIO) - 1));
        var warm6 = SCENE6_WARM_INIT;

        loader6.setCurrent(0, 1);

        if (localProgress >= SCENE6_PRELOAD_START) {
          warm6 = Math.max(warm6, first12);
        }
        if (localProgress > CROSS_SCENE_TAIL_EARLY) {
          warm6 = Math.max(
            warm6,
            Math.floor((localProgress - CROSS_SCENE_TAIL_EARLY) / (1 - CROSS_SCENE_TAIL_EARLY) * first12)
          );
        }
        if (localProgress > CROSS_SCENE_TAIL_START) {
          var tail6 = (localProgress - CROSS_SCENE_TAIL_START) / (1 - CROSS_SCENE_TAIL_START);
          warm6 = Math.max(warm6, first20);
          warm6 = Math.max(warm6, Math.ceil(tail6 * CROSS_SCENE6_WARM_MAX));
        }

        warm6 = Math.min(count6 - 1, warm6);
        loader6.warm(warm6);
        loader6.setCurrent(Math.min(warm6, count6 - 1), 1);
      } else if (nextIndex < total && localProgress >= CROSS_SCENE_TAIL_START) {
        var nextLoader = this.loaders[nextIndex];
        var nextCount = this.scenes[nextIndex].frameCount;
        var tail = (localProgress - CROSS_SCENE_TAIL_START) / (1 - CROSS_SCENE_TAIL_START);
        var warmEnd = Math.min(
          nextCount - 1,
          Math.max(FIRST_BURST, Math.ceil(tail * CROSS_SCENE_WARM_MAX))
        );
        nextLoader.warm(warmEnd);
        nextLoader.setCurrent(Math.min(warmEnd, nextCount - 1), 1);
      }

      if (nextIndex < total) {
        var boundary = (sceneIndex + 1) * sceneWeight;
        var lead = (nextIndex === 2 || nextIndex === 3 || nextIndex === 4 || nextIndex === 5) ? sceneWeight * 0.2 : sceneWeight * 0.12;
        if (globalProgress > boundary - lead) {
          var edgeLoader = this.loaders[nextIndex];
          var edgeCap = nextIndex === 5
            ? CROSS_SCENE6_WARM_MAX - 1
            : (nextIndex === 4
              ? CROSS_SCENE5_WARM_MAX - 1
              : (nextIndex === 3
                ? CROSS_SCENE4_WARM_MAX - 1
                : (nextIndex === 2 ? CROSS_SCENE3_WARM_MAX - 1 : CROSS_SCENE_WARM_MAX - 1)));
          var edgeWarm = Math.min(this.scenes[nextIndex].frameCount - 1, edgeCap);
          edgeLoader.warm(edgeWarm);
          edgeLoader.setCurrent(0, 1);
        }
      }
    } else {
      if (prevIndex >= 0) {
        var prevLoader = this.loaders[prevIndex];
        var prevLast = this.scenes[prevIndex].frameCount - 1;
        var boundaryBack = sceneIndex * sceneWeight;

        if (localProgress <= CROSS_SCENE_TAIL_START || globalProgress < boundaryBack + sceneWeight * 0.08) {
          var head = clamp(localProgress / CROSS_SCENE_TAIL_START, 0, 1);
          var warmMax = (prevIndex === 1 || prevIndex === 2)
            ? CROSS_SCENE3_WARM_MAX
            : ((prevIndex === 3 || prevIndex === 4)
              ? CROSS_SCENE4_WARM_MAX
              : (prevIndex === 5 ? CROSS_SCENE6_WARM_MAX : CROSS_SCENE_WARM_MAX));
          var warmBack = Math.min(
            prevLast,
            Math.max(FIRST_BURST, Math.ceil((1 - head) * warmMax))
          );
          prevLoader.warm(warmBack);
          prevLoader.setCurrent(Math.max(0, prevLast - warmBack), -1);
        }

        if (localProgress < 0.1) {
          prevLoader.setCurrent(prevLast, -1);
          prevLoader.warm(Math.min(prevLast, FIRST_BURST * 2));
        }
      }
    }
  };

  SequenceEngine.prototype.snapshotHold = function (video) {
    if (!video || !video.videoWidth || !video.videoHeight) return;
    if (!this.holdCanvas) this.holdCanvas = document.createElement('canvas');
    var c = this.holdCanvas;
    if (c.width !== video.videoWidth || c.height !== video.videoHeight) {
      c.width = video.videoWidth;
      c.height = video.videoHeight;
    }
    try {
      c.getContext('2d').drawImage(video, 0, 0);
      this.holdFrame = c;
    } catch (err) {}
  };

  SequenceEngine.prototype.paint = function (img) {
    var ctx = this.ctx;
    var cw = this.renderW || (this.canvas && this.canvas.width) || 0;
    var ch = this.renderH || (this.canvas && this.canvas.height) || 0;
    if (!ctx || !img || cw < 1 || ch < 1) return false;

    var iw = img.naturalWidth || img.videoWidth || img.width;
    var ih = img.naturalHeight || img.videoHeight || img.height;
    if (!iw || !ih) return false;

    var scale = this.isMobile ? Math.min(cw / iw, ch / ih) : Math.max(cw / iw, ch / ih);
    var dw = iw * scale;
    var dh = ih * scale;
    var x = (cw - dw) * this.posX;
    var y = (ch - dh) * this.posY;

    try {
      if (this.isMobile) {
        ctx.fillStyle = this.canvasBackground || '#eee8de';
        ctx.fillRect(0, 0, cw, ch);
      }
      ctx.drawImage(img, x, y, dw, dh);
    } catch (err) {
      return false;
    }
    return true;
  };

  SequenceEngine.prototype.draw = function (sceneIndex, frameIndex) {
    if (!this.ctx) return;

    if (this.useVideo) {
      var clip = this.clips[sceneIndex];
      var video = clip && clip.el;
      var canDraw = clip && clip.hasFrame();
      if (!canDraw) {
        var hold = this.holdFrame || this.lastImage;
        if (hold) this.paint(hold);
        return;
      }
      var drawKey = sceneIndex + ':' + video.currentTime.toFixed(3);
      if (!this.needsRedraw && drawKey === this.lastDrawnKey && !clip.seeking) return;
      if (this.paint(video)) {
        this.lastImage = video;
        this.lastDrawnKey = drawKey;
        this.lastPaintedScene = sceneIndex;
        this.activeSceneIndex = sceneIndex;
        this.needsRedraw = false;
        if (!this.markedReady) {
          this.markedReady = true;
          this.root.classList.add('is-ready');
        }
      }
      return;
    }

    if (!this.loaders.length) return;

    var loader = this.loaders[sceneIndex];
    if (!loader) return;

    var exact = loader.get(frameIndex);
    var img = exact;
    var drawnIndex = frameIndex;
    var crossing = this.lastPaintedScene >= 0 && sceneIndex !== this.lastPaintedScene;

    if (!img) {
      if (crossing && this.direction >= 0) {
        var first = loader.get(0);
        if (!first && this.lastImage) {
          this.paint(this.lastImage);
          this.needsRedraw = true;
          return;
        }
        if (first) {
          img = first;
          drawnIndex = 0;
        }
      } else if (crossing && this.direction < 0) {
        var lastIdx = loader.frameCount - 1;
        var last = loader.get(lastIdx);
        if (!last && this.lastImage) {
          this.paint(this.lastImage);
          this.needsRedraw = true;
          return;
        }
        if (last) {
          img = last;
          drawnIndex = lastIdx;
        }
      }

      if (!img) {
        var nearest = loader.getNearest(frameIndex);
        if (!nearest) {
          if (this.lastImage) this.paint(this.lastImage);
          return;
        }
        img = nearest.img;
        drawnIndex = nearest.index;
      }
    }

    var drawKey = sceneIndex + ':' + drawnIndex;
    if (!this.needsRedraw && drawKey === this.lastDrawnKey) return;

    if (this.paint(img)) {
      this.lastImage = img;
      this.lastDrawnKey = drawKey;
      this.lastDrawnIndex = drawnIndex;
      this.lastPaintedScene = sceneIndex;
      this.activeSceneIndex = sceneIndex;
      this.needsRedraw = false;
      if (!this.markedReady) {
        this.markedReady = true;
        this.root.classList.add('is-ready');
      }
    }
  };

  SequenceEngine.prototype.tick = function () {
    if (this.destroyed || !this.scenes.length) return false;

    if (this.needsMeasure) this.measure();
    if (!this.useVideo && this.needsCanvasResize) this.resizeCanvas();

    // The loading poster occupies one normal screen until a frame is decoded.
    // Its short geometry must not seek straight to the final scene on a swipe.
    var target = this.useVideo && !this.markedReady ? 0 : this.targetProgress;
    var current = this.currentProgress;
    var gap = Math.abs(target - current);

    if (this.reducedMotion) {
      current = target;
    } else {
      var ease = this.useVideo
        ? (gap > 0.012 ? 0.48 : 0.32)
        : (gap > 0.04 ? clamp(this.easing * 2.4, 0.16, 0.48) : this.easing);
      current += (target - current) * ease;
      if (Math.abs(target - current) < (this.useVideo ? 0.00012 : SETTLE_EPS)) current = target;
      gap = Math.abs(target - current);
    }

    this.currentProgress = current;

    var visual = resolveGlobal(current, this.scenes.length);
    var load = resolveGlobal(target, this.scenes.length);
    var visualScene = this.scenes[visual.sceneIndex];
    var loadScene = this.scenes[load.sceneIndex];
    var frameIndex = alignFrame(
      Math.round(visual.localProgress * (visualScene.frameCount - 1)),
      visualScene.frameCount
    );
    var loadIndex = alignFrame(
      Math.round(load.localProgress * (loadScene.frameCount - 1)),
      loadScene.frameCount
    );

    this.activeSceneIndex = visual.sceneIndex;
    this.frameIndex = frameIndex;

    if (this.useVideo) {
      // Media callbacks publish decoded/presented progress. Waiting for a load or
      // seek must not keep the global animation loop running.
      if (this.player) this.player.setProgress(visual.sceneIndex, visual.localProgress);
      return current !== target;
    }

    if (this.copy) this.copy.sync(visual.sceneIndex, visual.localProgress);

    this.syncLoaderPriority(load.sceneIndex);

    if (this.loaders[load.sceneIndex]) {
      this.loaders[load.sceneIndex].setCurrent(loadIndex, this.direction);
    }
    if (visual.sceneIndex !== load.sceneIndex && this.loaders[visual.sceneIndex]) {
      this.loaders[visual.sceneIndex].setCurrent(frameIndex, this.direction);
    }

    this.updateCrossScenePreload(target, load.sceneIndex, load.localProgress);
    this.draw(visual.sceneIndex, frameIndex);

    return current !== target || this.needsRedraw;
  };

  SequenceEngine.prototype.destroy = function () {
    this.destroyed = true;
    unbindMedia(reducedMq, this.onReduced);
    if (this.ro) {
      this.ro.disconnect();
      this.ro = null;
    }
    var i;
    for (i = 0; i < this.loaders.length; i++) {
      this.loaders[i].destroy();
    }
    this.loaders = [];
    for (i = 0; i < this.clips.length; i++) {
      this.clips[i].destroy();
    }
    this.clips = [];
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    if (this.copy) {
      this.copy.destroy();
      this.copy = null;
    }
    this.holdFrame = null;
    this.holdCanvas = null;
    this.lastImage = null;
    this.ctx = null;
    this.canvas = null;
    this.sticky = null;
    this.root.classList.remove('is-ready', 'is-video');
    this.root.removeAttribute('data-lf-sequence-bound');
    this.root = null;
  };

  function initRoot(root) {
    if (!root || root.getAttribute('data-lf-sequence-bound') === '1') return;
    root.setAttribute('data-lf-sequence-bound', '1');
    var engine = new SequenceEngine(root);
    engines.push(engine);
    bindGlobals();
    engine.measure();
    if (!engine.useVideo) engine.resizeCanvas();
    engine.syncScroll(window.pageYOffset || document.documentElement.scrollTop || 0);
    startRaf();
  }

  function destroyRoot(root) {
    var i;
    for (i = engines.length - 1; i >= 0; i--) {
      if (engines[i].root === root) {
        engines[i].destroy();
        engines.splice(i, 1);
      }
    }
    if (!engines.length) unbindGlobals();
  }

  function initIn(scope) {
    if (!scope) return;
    if (scope.getAttribute && scope.getAttribute('data-lf-sequence') != null) initRoot(scope);
    var nodes = scope.querySelectorAll ? scope.querySelectorAll('[data-lf-sequence]') : [];
    var i;
    for (i = 0; i < nodes.length; i++) initRoot(nodes[i]);
  }

  function destroyIn(scope) {
    if (!scope) return;
    if (scope.getAttribute && scope.getAttribute('data-lf-sequence') != null) destroyRoot(scope);
    var nodes = scope.querySelectorAll ? scope.querySelectorAll('[data-lf-sequence]') : [];
    var i;
    for (i = 0; i < nodes.length; i++) destroyRoot(nodes[i]);
  }

  function onScroll() {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var i;
    for (i = 0; i < engines.length; i++) engines[i].syncScroll(y);
    startRaf();
  }

  function onResize() {
    var i;
    for (i = 0; i < engines.length; i++) {
      engines[i].needsMeasure = true;
      engines[i].needsCanvasResize = true;
    }
    startRaf();
  }

  function loop() {
    var keep = false;
    var i;
    for (i = 0; i < engines.length; i++) {
      if (engines[i].tick()) keep = true;
    }
    rafId = keep ? window.requestAnimationFrame(loop) : 0;
  }

  function startRaf() {
    if (rafId || !engines.length) return;
    rafId = window.requestAnimationFrame(loop);
  }

  function bindGlobals() {
    if (scrollBound) return;
    scrollBound = true;
    previousScrollBehavior = document.documentElement.style.getPropertyValue('scroll-behavior');
    previousScrollPriority = document.documentElement.style.getPropertyPriority('scroll-behavior');
    document.documentElement.style.setProperty('scroll-behavior', 'auto', 'important');
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onResize);
  }

  function unbindGlobals() {
    if (!scrollBound) return;
    scrollBound = false;
    if (previousScrollBehavior) {
      document.documentElement.style.setProperty('scroll-behavior', previousScrollBehavior, previousScrollPriority);
    } else {
      document.documentElement.style.removeProperty('scroll-behavior');
    }
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('load', onResize);
    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = 0;
    }
  }

  function boot() {
    initIn(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initIn(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyIn(event.target);
  });

  document.addEventListener('shopify:section:reorder', onResize);
  document.addEventListener('shopify:section:select', onResize);

  window.LFImageSequence = {
    initIn: initIn,
    destroyIn: destroyIn
  };
})(window, document);
